import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { SITE_NAME } from '../../constants/site';
import { ALL_CATEGORIES } from '../../constants/blogCategories';
import { toSlug } from '../../utils/slug';
import SearchBox from './SearchBox';
import ThemeToggle from './ThemeToggle';

const PRIMARY_LINKS = [
  { to: '/', label: 'Home', end: true },
  { to: '/blogs', label: 'Articles' },
  { to: '/about', label: 'About' },
];

function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const panelRef = useRef(null);

  // Close the drawer on navigation — otherwise it stays open over the new page.
  useEffect(() => setOpen(false), [pathname]);

  // Escape closes it, and body scroll is locked while it covers the page.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    document.body.classList.add('is-nav-open');
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('is-nav-open');
    };
  }, [open]);

  return (
    <header className="site-header">
      <div className="container site-header-inner">
        <Link to="/" className="brand" aria-label={`${SITE_NAME} home`}>
          <span className="brand-mark" aria-hidden="true">
            {SITE_NAME.charAt(0)}
          </span>
          <span className="brand-name">{SITE_NAME}</span>
        </Link>

        <nav className="site-nav" aria-label="Main">
          {PRIMARY_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className="nav-link">
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="header-actions">
          <SearchBox className="search-box-header" id="header-search" />
          <ThemeToggle />
          <button
            type="button"
            className="icon-btn nav-toggle"
            aria-label="Open menu"
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((v) => !v)}
          >
            <svg className="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile drawer. Always rendered so its links are in the prerendered
          HTML — hidden with CSS rather than unmounted. */}
      <div
        id="mobile-nav"
        ref={panelRef}
        className={`mobile-nav ${open ? 'is-open' : ''}`}
        hidden={!open}
      >
        <div className="container">
          <SearchBox className="search-box-mobile" id="mobile-search" />
          <nav aria-label="Mobile">
            {PRIMARY_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className="mobile-nav-link">
                {l.label}
              </NavLink>
            ))}
            <span className="mobile-nav-heading">Categories</span>
            {ALL_CATEGORIES.map((c) => (
              <NavLink key={c} to={`/category/${toSlug(c)}`} className="mobile-nav-link">
                {c}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {open && (
        <button
          type="button"
          className="nav-scrim"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
        />
      )}
    </header>
  );
}

export default Header;
